export const isUUID = (str) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const daysAgo = (utcDate) => {
  const date = new Date(utcDate);
  const now = new Date();
  const differenceInMilliseconds = now - date;
  const millisecondsInADay = 1000 * 60 * 60 * 24;
  const daysAgo = Math.floor(differenceInMilliseconds / millisecondsInADay);
  return daysAgo;
};

export function formatTeamsCall(input) {
  const match = String(input ?? "").match(/(.*),,(\d+#)$/);

  if (!match) {
    return {
      original: input,
      formatted: "",
      error: "Teams meeting number must end with ,,[Conference ID]# format",
    };
  }

  const [, phoneNumber, conferenceId] = match;
  const { formatted, error } = validateUsPhoneNumber(phoneNumber);
  if (error) {
    return { original: input, formatted: null, error };
  }

  return {
    original: input,
    formatted: `${formatted}ww${conferenceId}`,
    error: null,
  };
}

export const PHONE_ERROR_MESSAGES = {
  empty: "Please add a phone number",
  tooShort: "Phone number is too short — expected 10 digits (e.g. 3135551234)",
  tooLong: "Phone number is too long — expected 10 digits (e.g. 3135551234)",
  nonUs: "Only US phone numbers are supported (+1)",
  invalidNanp:
    "Not a valid US number — area code and exchange must start with 2-9",
};

// NANP: area code and exchange code both begin with 2-9.
export const NANP_SUBSCRIBER_PATTERN = /^[2-9]\d{2}[2-9]\d{6}$/;

export const sanitizePhoneInput = (value) => {
  const raw = String(value ?? "");
  const digits = raw.replace(/\D/g, "");
  return raw.trimStart().startsWith("+") ? `+${digits}` : digits;
};

export const validateUsPhoneNumber = (value) => {
  const raw = String(value ?? "").trim();
  const digits = raw.replace(/\D/g, "");

  if (!digits) {
    return { formatted: null, error: PHONE_ERROR_MESSAGES.empty };
  }

  // A NANP area code never starts with 1, so a leading 1 is always the country code.
  const hasCountryCode = digits.startsWith("1");
  const subscriber = hasCountryCode ? digits.slice(1) : digits;

  if (subscriber.length === 10) {
    if (!NANP_SUBSCRIBER_PATTERN.test(subscriber)) {
      return { formatted: null, error: PHONE_ERROR_MESSAGES.invalidNanp };
    }
    return { formatted: `+1${subscriber}`, error: null };
  }
  if (!hasCountryCode && (raw.startsWith("+") || subscriber.length === 11)) {
    return { formatted: null, error: PHONE_ERROR_MESSAGES.nonUs };
  }
  if (subscriber.length < 10) {
    return { formatted: null, error: PHONE_ERROR_MESSAGES.tooShort };
  }
  return { formatted: null, error: PHONE_ERROR_MESSAGES.tooLong };
};

// Teams dial strings carry a ",,<conference id>#" suffix that must survive validation.
export const validateDialTarget = (input, type) => {
  const raw = String(input ?? "");
  if (raw.includes(",,") && raw.length > 11 && type !== "text") {
    return formatTeamsCall(raw);
  }
  return validateUsPhoneNumber(raw);
};

const isTeamsDialString = (value) => String(value ?? "").includes(",");

// Sanitizing would strip a Teams conference id, so those are passed through untouched.
export const preparePhoneInput = (value) =>
  isTeamsDialString(value) ? String(value ?? "") : sanitizePhoneInput(value);

// Empty and Teams values defer to the submit check rather than flagging the field.
export const applyPhoneBlur = (value) => {
  if (!value || isTeamsDialString(value)) {
    return { value, error: null };
  }

  const { formatted, error } = validateUsPhoneNumber(value);
  return { value: formatted ?? value, error };
};

export const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const simpleStringHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash.toString(16);
};

export function formatAgo(date) {
  const now = new Date();
  const diff = now - date;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) {
    return "now";
  } else if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else if (hours < 48) {
    return "yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
